import { useState } from 'react'
import { Ellipsis } from 'lucide-react'
import { clusters as api } from '@/api/endpoints'
import type { Cluster } from '@/api/types'
import { Err } from '@/components/Err'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

// 클러스터 카드 머리 오른쪽의 메뉴. 클러스터에 하는 조작은 이 둘이 전부다 — 이름 바꾸기 · 클러스터 제거(응답하는 멤버가 없을 때만).
// 결과는 카드가 말한다: 새 이름이 바로 서고, 제거된 카드는 사라진다. 콘솔 사용자는 시스템이 만든다 — 여기에 입력이 없다.

type Open = 'rename' | 'remove' | null

export function ClusterMenu({ cluster, name, onRenamed, onRemoved }: { cluster: Cluster; name: string; onRenamed: (name: string) => void; onRemoved: () => void }) {
  const [open, setOpen] = useState<Open>(null)
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="size-7 flex-none text-muted-foreground" aria-label={`${name} 관리`}>
            <Ellipsis className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => setOpen('rename')}>이름 바꾸기</DropdownMenuItem>
          {cluster.live ? null : (
            <DropdownMenuItem variant="destructive" className="max-sm:hidden" onSelect={() => setOpen('remove')}>
              클러스터 제거
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <RenameDialog cluster={cluster} name={name} open={open === 'rename'} onClose={() => setOpen(null)} onRenamed={onRenamed} />
      <RemoveDialog cluster={cluster} open={open === 'remove'} onClose={() => setOpen(null)} onRemoved={onRemoved} />
    </>
  )
}

function RenameDialog({ cluster, name, open, onClose, onRenamed }: { cluster: Cluster; name: string; open: boolean; onClose: () => void; onRenamed: (name: string) => void }) {
  return (
    <Dialog open={open} onOpenChange={(v) => (v ? undefined : onClose())}>
      <DialogContent aria-describedby={undefined}>{open ? <RenameForm cluster={cluster} name={name} onClose={onClose} onRenamed={onRenamed} /> : null}</DialogContent>
    </Dialog>
  )
}

function RenameForm({ cluster, name, onClose, onRenamed }: { cluster: Cluster; name: string; onClose: () => void; onRenamed: (name: string) => void }) {
  const [text, setText] = useState(name)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const save = async () => {
    const next = text.trim()
    if (!next) return
    setBusy(true)
    setError(null)
    try {
      const updated = await api.rename(cluster.id, next)
      onRenamed(updated.name)
      onClose()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }
  return (
    <>
      <DialogHeader>
        <DialogTitle>이름 바꾸기</DialogTitle>
      </DialogHeader>
      <div className="grid gap-2">
        <Label htmlFor="cluster-rename">이름</Label>
        <Input id="cluster-rename" maxLength={63} value={text} onChange={(event) => setText(event.target.value)} />
      </div>
      <Err error={error} />
      <DialogFooter>
        <Button variant="outline" disabled={busy} onClick={onClose}>
          취소
        </Button>
        <Button disabled={busy || !text.trim()} onClick={() => void save()}>
          {busy ? '저장 중' : '저장'}
        </Button>
      </DialogFooter>
    </>
  )
}

function RemoveDialog({ cluster, open, onClose, onRemoved }: { cluster: Cluster; open: boolean; onClose: () => void; onRemoved: () => void }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const remove = async () => {
    setBusy(true)
    setError(null)
    try {
      await api.remove(cluster.id)
      onClose()
      onRemoved()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Dialog open={open} onOpenChange={(v) => (v || busy ? undefined : (setError(null), onClose()))}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>클러스터 제거</DialogTitle>
        </DialogHeader>
        <Err error={error} />
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={() => (setError(null), onClose())}>
            취소
          </Button>
          <Button variant="destructive" disabled={busy} onClick={() => void remove()}>
            {busy ? '제거 중' : '제거'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
